import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = checking, null = guest, object = authenticated user
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(null);
      return undefined;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (!cancelled) setUser(data);
      })
      .catch((err) => {
        // Token caducado/revocado: limpiar la sesión stale (solo en 401 —
        // un error de red transitorio no debe desloguear al usuario).
        if (err?.response?.status === 401) localStorage.removeItem(TOKEN_KEY);
        if (!cancelled) setUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password, extra = {}) => {
    const { data } = await api.post("/auth/login", { email: email.trim(), password, ...extra });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password, extra = {}) => {
    const { data } = await api.post("/auth/register", { name: name.trim(), email: email.trim(), password, ...extra });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // token may already be invalid — always clear local state
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  // Re-fetch the profile (e.g. after the wizard upgrades a buyer to organizer)
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
